import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import introJs from 'intro.js';
import type { TourStep } from 'intro.js/src/packages/tour/steps';
import { Observable } from 'rxjs';
import { LocalStorageService } from '../../../gamma/localstorage';
import { U2000LS_IntroDontShowAgain } from '../U2000_localstorages';

/**
 * A tour step, as built by callers of {@link U2000_IntroJSService.start} — only a subset of
 * intro.js 8's `TourStep` fields are ever populated (`element`/`title`/`intro`), matching
 * `TourOptions.steps: Partial<TourStep>[]`. Re-exported here so callers don't depend on
 * intro.js's internal module layout directly (it moved between v7 and v8).
 */
export type U2000_IntroStep = Partial<TourStep>;

@Injectable({ providedIn: 'root' })
export class U2000_IntroJSService {
    constructor(
        private translateService: TranslateService, //
        private localStorageService: LocalStorageService,
    ) {}

    introJS: ReturnType<typeof introJs> = null;

    start(id: string, steps: U2000_IntroStep[], force = false) {
        return new Observable<void>(subscriber => {
            const lsDontShowAgain = U2000LS_IntroDontShowAgain + '_' + id;
            if (this.localStorageService.get(lsDontShowAgain, false) === false || force) {
                this.introJS = introJs();

                this.introJS.onafterchange(x => {
                    const intervalID = setInterval(() => {
                        let check = document.getElementById('introjs-dontShowAgain') as HTMLInputElement;

                        // intro.js reutilise le conteneur du tooltip entre les etapes : l'ancienne case a
                        // cocher reste dans le DOM (cachee) jusqu'a ce que le nouveau contenu la remplace,
                        // ~350ms plus tard (animation de fondu). On attend donc qu'elle soit visible avant
                        // d'y attacher le listener, sinon celui-ci est attache a un noeud sur le point d'etre
                        // detruit. checkVisibility() n'est pas supporte partout (Safari < 17.4, Edge fige) ;
                        // offsetParent sert de repli equivalent.
                        const isVisible = check != null && (typeof check['checkVisibility'] === 'function' ? check['checkVisibility']() : check.offsetParent !== null);

                        if (isVisible) {
                            check.checked = this.localStorageService.get(lsDontShowAgain, false) as any;
                            check.addEventListener('change', e => {
                                this.localStorageService.set(lsDontShowAgain, check.checked);
                            });
                            window.clearInterval(intervalID);
                        }
                    }, 100);
                });

                this.introJS.onexit(() => {
                    this.introJS = null;

                    subscriber.next();
                    subscriber.complete();
                    /* document.removeEventListener('keypress', this.stopEscapePropagation); */
                });

                const modifiedSteps = JSON.parse(JSON.stringify(steps)) as U2000_IntroStep[];
                modifiedSteps.forEach(x => {
                    x.intro =
                        x.intro +
                        `</br></br><div class="introjs-dontShowAgain"><input type="checkbox" id="introjs-dontShowAgain" name="introjs-dontShowAgain"><label for="introjs-dontShowAgain">Ne plus afficher au démarrage</label></div>`;
                });

                this.introJS
                    .setOptions({
                        exitOnEsc: false,
                        exitOnOverlayClick: false,
                        disableInteraction: true,
                        nextLabel: this.translateService.instant('lblNext'),
                        prevLabel: this.translateService.instant('lblPrevious'),
                        skipLabel: '',
                        doneLabel: this.translateService.instant('lblDone'),
                        steps: modifiedSteps,
                    })
                    .start();
            } else {
                subscriber.next();
                subscriber.complete();
            }
        });
    }

    isStarted() {
        return this.introJS != null;
    }

    /* private stopEscapePropagation(event: KeyboardEvent) {
        event.stopPropagation();
        event.preventDefault();
        return false;
    } */
}
